import React from "react";
import { motion } from "framer-motion";
import { BadgeCheck, Briefcase, HeartHandshake, IdCard, ArrowUpRight } from "lucide-react";
import { useConsultation } from "../context/ConsultationContext";

const TEAM_IMG =
  "https://images.pexels.com/photos/7793999/pexels-photo-7793999.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const SERVICES = [
  {
    icon: BadgeCheck,
    title: "Investor / Partner Visa",
    copy: "Secure your 2-year or 10-year UAE residency as a founder or shareholder.",
  },
  {
    icon: Briefcase,
    title: "Employee Work Permits",
    copy: "Seamless MOHRE & immigration onboarding for your global talent.",
  },
  {
    icon: HeartHandshake,
    title: "Family Sponsorship",
    copy: "Bring your spouse, children and parents — zero paperwork stress.",
  },
  {
    icon: IdCard,
    title: "Emirates ID & Medical",
    copy: "We escort you through every mandatory government step, end to end.",
  },
];

export default function VisaServicesSection() {
  const { open } = useConsultation();

  return (
    <section
      data-testid="visa-services-section"
      className="relative bg-[#0A1128] text-white py-20 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="container-x relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <p className="overline text-[#6B8AFF]">Visa & Residency</p>
            <div className="gold-rule mt-5 mb-6" />
            <h2 className="font-heading font-light tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight">
              Your path to <span className="font-black text-[#6B8AFF]">Golden</span>{" "}
              Residency and beyond.
            </h2>
            <p className="mt-6 text-slate-300/90 text-base md:text-lg leading-relaxed max-w-2xl">
              Residency in the UAE is more than just a permit — it's an invitation to a
              world-class lifestyle and business stability. StartINC handles the
              end-to-end immigration cycle for founders, their families and their teams.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-sm overflow-hidden border border-white/10">
              <img
                src={TEAM_IMG}
                alt="Diverse professional team"
                className="w-full h-64 md:h-72 object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128] via-transparent" />
              <div className="absolute left-5 bottom-5 right-5 flex items-center justify-between">
                <span className="overline text-white/80">Backed by ex-government liaisons</span>
                <ArrowUpRight className="w-5 h-5 text-[#6B8AFF]" />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {SERVICES.map(({ icon: Icon, title, copy }, i) => (
            <motion.div
              key={title}
              data-testid={`visa-service-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="card-dark rounded-sm p-6 md:p-7 h-full flex flex-col group"
            >
              <Icon className="w-8 h-8 text-[#6B8AFF]" strokeWidth={1.5} />
              <h3 className="mt-5 font-heading font-semibold text-lg tracking-tight">
                {title}
              </h3>
              <p className="mt-2 text-sm text-slate-300/80 leading-relaxed">{copy}</p>
              <div className="mt-auto pt-6 flex items-center gap-2 text-[#6B8AFF] text-sm font-heading opacity-0 group-hover:opacity-100 transition-opacity">
                Learn more <ArrowUpRight className="w-4 h-4" />
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap gap-3">
          <button
            data-testid="visa-consult-cta"
            onClick={() => open("visa")}
            className="btn-gold"
          >
            Start your residency today <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
